import React, { useState, useEffect, useCallback } from "react";
import { createClient } from "@supabase/supabase-js";
import { Card, Row, Col, Form, Button, InputGroup, Table } from "react-bootstrap";
import { listaMasajes } from "../../assets/data/masajes";

const { REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY } = process.env;
const supabase = createClient(REACT_APP_SUPABASE_URL, REACT_APP_SUPABASE_KEY);

const AdminEstadisticas = () => {
    const hoy = new Date();
    const mesActualStr = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;

    const [mesSeleccionado, setMesSeleccionado] = useState(mesActualStr);
    const [citasDelMes, setCitasDelMes] = useState([]);
    const [gastosDelMes, setGastosDelMes] = useState([]);
    
    const [nombreGastoInput, setNombreGastoInput] = useState("");
    const [totalGastoInput, setTotalGastoInput] = useState("");
    const [editandoGastoId, setEditandoGastoId] = useState(null);

    const [loadingCitas, setLoadingCitas] = useState(false);
    const [loadingGastos, setLoadingGastos] = useState(false);
    const [mostrarLista, setMostrarLista] = useState(false);

    // Funcion auxiliar para buscar el masaje de forma flexible por nombre o titulo alternativo
    const buscarMasajeEnLista = (tipoDeMasaje) => {
        if (!tipoDeMasaje) return null;
        const tipoCita = tipoDeMasaje.trim().toLowerCase();
        
        return listaMasajes.find((m) => {
            const titleLower = m.title?.trim().toLowerCase() || "";
            const altTitleLower = m.altTitle?.trim().toLowerCase() || "";
            
            return titleLower === tipoCita || 
                   altTitleLower === tipoCita || 
                   tipoCita.includes(titleLower) || 
                   titleLower.includes(tipoCita);
        });
    };

    // Recogida del precio oficial basado en masajes.js
    const obtenerPrecioCita = (cita) => {
        if (cita.precio !== undefined && cita.precio !== null) {
            return Number(cita.precio);
        }
        
        const masajeOficial = buscarMasajeEnLista(cita.tipo_de_masaje);
        if (masajeOficial) {
            return Number(masajeOficial.precio);
        }

        if (cita.duracion === 60) return 50;
        if (cita.duracion === 90) return 75;
        if (cita.duracion === 45) return 35;
        if (cita.duracion === 30) return 30;
        if (cita.duracion === 25) return 25;
        return 0;
    };

    // NUEVA FUNCION: Recogida de la duracion oficial basada en masajes.js
    const obtenerDuracionCita = (cita) => {
        const masajeOficial = buscarMasajeEnLista(cita.tipo_de_masaje);
        
        if (masajeOficial && masajeOficial.altTitle) {
            const altTitle = argumento => masajeOficial.altTitle;
            if (masajeOficial.altTitle.includes("1h30min")) return "1h 30min";
            if (masajeOficial.altTitle.includes("45min")) return "45 min";
            if (masajeOficial.altTitle.includes("25min")) return "25 min";
        }
        
        // Si no se encuentra en el archivo .js, recurre al valor de la base de datos
        return cita.duracion ? `${cita.duracion} min` : "-";
    };

    // 1. Cargar las citas de todo el mes desde Supabase
    const fetchCitasMes = useCallback(async () => {
        setLoadingCitas(true);
        const [year, monthStr] = mesSeleccionado.split("-");
        const month = parseInt(monthStr, 10) - 1;

        const primerDia = new Date(year, month, 1, 0, 0, 0, 0).toISOString();
        const ultimoDia = new Date(year, month + 1, 0, 23, 59, 59, 999).toISOString();

        const { data, error } = await supabase
            .from("citas")
            .select("*")
            .neq("tipo_de_masaje", "Bloqueado")
            .gte("hora", primerDia)
            .lte("hora", ultimoDia);

        if (!error) {
            setCitasDelMes(data || []);
        }
        setLoadingCitas(false);
    }, [mesSeleccionado]);

    // 2. Cargar todos los gastos individuales del mes desde Supabase
    const fetchGastosMes = useCallback(async () => {
        setLoadingGastos(true);
        const { data, error } = await supabase
            .from("gastos")
            .select("*")
            .eq("mes", mesSeleccionado)
            .order("created_at", { ascending: true });

        if (!error) {
            setGastosDelMes(data || []);
        }
        setLoadingGastos(false);
    }, [mesSeleccionado]);

    useEffect(() => {
        fetchCitasMes();
        fetchGastosMes();
        cancelarEdicion();
    }, [mesSeleccionado, fetchCitasMes, fetchGastosMes]);

    // 3. Guardar nuevo gasto o actualizar uno existente
    const guardarGasto = async (e) => {
        e.preventDefault();
        if (!nombreGastoInput.trim() || !totalGastoInput) return;

        const valorTotal = Number(totalGastoInput) || 0;
        const payload = {
            mes: mesSeleccionado,
            nombre: nombreGastoInput.trim(),
            total: valorTotal
        };

        if (editandoGastoId) {
            const { error } = await supabase
                .from("gastos")
                .update(payload)
                .eq("id", editandoGastoId);

            if (error) {
                alert("Error al actualizar el gasto: " + error.message);
            } else {
                cancelarEdicion();
                fetchGastosMes();
            }
        } else {
            const { error } = await supabase
                .from("gastos")
                .insert(payload);

            if (error) {
                alert("Error al guardar el gasto: " + error.message);
            } else {
                setNombreGastoInput("");
                setTotalGastoInput("");
                fetchGastosMes();
            }
        }
    };

    // 4. Eliminar un gasto de la base de datos
    const eliminarGasto = async (id) => {
        if (!window.confirm("¿Seguro que quieres eliminar este gasto?")) return;
        const { error } = await supabase
            .from("gastos")
            .delete()
            .eq("id", id);

        if (error) {
            alert("Error al eliminar el gasto: " + error.message);
        } else {
            fetchGastosMes();
            if (editandoGastoId === id) {
                cancelarEdicion();
            }
        }
    };

    const iniciarEdicion = (gasto) => {
        setEditandoGastoId(gasto.id);
        setNombreGastoInput(gasto.nombre);
        setTotalGastoInput(gasto.total.toString());
    };

    const cancelarEdicion = () => {
        setEditandoGastoId(null);
        setNombreGastoInput("");
        setTotalGastoInput("");
    };

    // Calculos globales de rendimiento
    const dineroGanado = citasDelMes
        .filter((c) => c.ha_pagado)
        .reduce((acc, c) => acc + obtenerPrecioCita(c), 0);

    const dineroPendiente = citasDelMes
        .filter((c) => !c.ha_pagado)
        .reduce((acc, c) => acc + obtenerPrecioCita(c), 0);

    const dineroPosibleTotal = dineroGanado + dineroPendiente;
    const totalGastosAcumulados = gastosDelMes.reduce((acc, g) => acc + Number(g.total), 0);
    
    const balanceReal = dineroGanado - totalGastosAcumulados;
    const balancePotencial = dineroPosibleTotal - totalGastosAcumulados;

    const citasOrdenadas = [...citasDelMes].sort((a, b) => new Date(a.hora) - new Date(b.hora));

    return (
        <Card className="shadow-sm border-0 mt-4 mb-5" style={{ background: "#fdfdfd" }}>
            <Card.Body style={{ padding: "30px" }}>
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
                    <div>
                        <h4 style={{ fontWeight: "700", color: "#343a40", margin: 0 }}>
                            Cuadro de Mando y Estadística Mensual
                        </h4>
                        <p className="text-muted m-0">Control de ingresos, gastos y rendimiento sincronizado con la nube.</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <Form.Control
                            type="month"
                            value={mesSeleccionado}
                            onChange={(e) => setMesSeleccionado(e.target.value)}
                            style={{ width: "auto", fontWeight: "500" }}
                        />
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => { fetchCitasMes(); fetchGastosMes(); }} 
                            disabled={loadingCitas || loadingGastos}
                        >
                            {loadingCitas || loadingGastos ? "..." : "Recargar"}
                        </Button>
                    </div>
                </div>

                {/* Bloque de Tarjetas de Metricas */}
                <Row className="g-3 text-center mb-4">
                    <Col md={4}>
                        <div style={{ background: "#e8f5e9", padding: "20px", borderRadius: "10px" }}>
                            <span style={{ fontSize: "0.9rem", color: "#2e7d32", fontWeight: "600" }}>GANADO AL MES (CONFIRMADO)</span>
                            <h2 style={{ fontSize: "2.2rem", fontWeight: "bold", color: "#1b5e20", marginTop: "5px" }}>
                                {dineroGanado}€
                            </h2>
                            <small className="text-muted">Citas cobradas actualmente</small>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div style={{ background: "#e3f2fd", padding: "20px", borderRadius: "10px" }}>
                            <span style={{ fontSize: "0.9rem", color: "#1565c0", fontWeight: "600" }}>INGRESOS ESTIMADOS (TOTAL POSIBLE)</span>
                            <h2 style={{ fontSize: "2.2rem", fontWeight: "bold", color: "#0d47a1", marginTop: "5px" }}>
                                {dineroPosibleTotal}€
                            </h2>
                            <small className="text-muted">Cobrado + pendiente de pago</small>
                        </div>
                    </Col>
                    <Col md={4}>
                        <div style={{ background: "#fff3e0", padding: "20px", borderRadius: "10px" }}>
                            <span style={{ fontSize: "0.9rem", color: "#e65100", fontWeight: "600" }}>PENDIENTE DE COBRO</span>
                            <h2 style={{ fontSize: "2.2rem", fontWeight: "bold", color: "#bf360c", marginTop: "5px" }}>
                                {dineroPendiente}€
                            </h2>
                            <small className="text-muted">Por asegurar en caja</small>
                        </div>
                    </Col>
                </Row>

                <hr style={{ borderTop: "1px solid #dee2e6", margin: "30px 0" }} />

                {/* Calculadora de Gastos y Beneficios Netos */}
                <Row className="g-4 align-items-start mb-4">
                    <Col lg={6}>
                        <h5 style={{ fontWeight: "600", marginBottom: "15px" }}>
                            {editandoGastoId ? "Modificar Gasto seleccionado" : "Añadir concepto de Gasto"}
                        </h5>
                        <Form onSubmit={guardarGasto} className="mb-3">
                            <Row className="g-2">
                                <Col sm={6}>
                                    <Form.Control
                                        type="text"
                                        placeholder="Concepto (ej: Alquiler, Aceites)"
                                        value={nombreGastoInput}
                                        onChange={(e) => setNombreGastoInput(e.target.value)}
                                        required
                                    />
                                </Col>
                                <Col sm={4}>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            placeholder="Importe"
                                            value={totalGastoInput}
                                            onChange={(e) => setTotalGastoInput(e.target.value)}
                                            required
                                        />
                                        <InputGroup.Text>€</InputGroup.Text>
                                    </InputGroup>
                                </Col>
                                <Col sm={2} className="d-flex gap-1">
                                    <Button variant="dark" type="submit" className="w-100">
                                        {editandoGastoId ? "Ok" : "Añadir"}
                                    </Button>
                                    {editandoGastoId && (
                                        <Button variant="outline-secondary" onClick={cancelarEdicion}>
                                            X
                                        </Button>
                                    )}
                                </Col>
                            </Row>
                        </Form>

                        <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #dee2e6", borderRadius: "6px" }}>
                            <Table size="sm" hover className="m-0" style={{ fontSize: "0.85rem" }}>
                                <thead className="table-light">
                                    <tr>
                                        <th>Concepto</th>
                                        <th className="text-end">Importe</th>
                                        <th className="text-end">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {gastosDelMes.length === 0 ? (
                                        <tr>
                                            <td colSpan="3" className="text-muted text-center py-2" style={{ fontStyle: "italic" }}>
                                                No hay gastos añadidos en este mes.
                                            </td>
                                        </tr>
                                    ) : (
                                        gastosDelMes.map((g) => (
                                            <tr key={g.id} style={{ verticalAlign: "middle" }}>
                                                <td>{g.nombre}</td>
                                                <td className="text-end" style={{ fontWeight: "600" }}>{g.total}€</td>
                                                <td className="text-end">
                                                    <Button variant="link" size="sm" className="text-primary me-1 p-0 text-decoration-none" onClick={() => iniciarEdicion(g)}>
                                                        Editar
                                                    </Button>
                                                    <Button variant="link" size="sm" className="text-danger p-0 text-decoration-none" onClick={() => eliminarGasto(g.id)}>
                                                        Eliminar
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        <div className="text-end mt-2" style={{ fontWeight: "600", fontSize: "0.9rem" }}>
                            Total Gastos del Mes: <span className="text-danger">{totalGastosAcumulados}€</span>
                        </div>
                    </Col>

                    <Col lg={6}>
                        <h5 style={{ fontWeight: "600", marginBottom: "15px" }}>Cálculo de Beneficios Netos (Diferencia)</h5>
                        <div style={{ border: "1px solid #dee2e6", borderRadius: "8px", padding: "20px", background: "#fdfdfd" }}>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-secondary">Beneficio Neto Real (Cobradas - Gastos):</span>
                                <span style={{ fontWeight: "700", color: balanceReal >= 0 ? "#2e7d32" : "#c62828", fontSize: "1.3rem" }}>
                                    {balanceReal}€
                                </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="text-secondary">Beneficio Neto Potencial (Todo - Gastos):</span>
                                <span style={{ fontWeight: "700", color: balancePotencial >= 0 ? "#1565c0" : "#c62828", fontSize: "1.3rem" }}>
                                    {balancePotencial}€
                                </span>
                            </div>
                        </div>
                    </Col>
                </Row>

                <hr style={{ borderTop: "1px solid #dee2e6", margin: "30px 0" }} />

                {/* SECCION DESPLEGABLE: Listado de masajes del mes */}
                <div className="mt-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <h5 style={{ fontWeight: "600", margin: 0 }} className="d-flex align-items-center gap-2">
                            <span>Masajes Contabilizados en el Mes</span>
                            <span className="badge bg-secondary" style={{ fontSize: "0.85rem" }}>
                                {citasDelMes.length} {citasDelMes.length === 1 ? "cita" : "citas"}
                            </span>
                        </h5>
                        
                        <Button 
                            variant={mostrarLista ? "outline-secondary" : "primary"} 
                            size="sm" 
                            onClick={() => setMostrarLista(!mostrarLista)}
                            style={{ fontWeight: "500" }}
                        >
                            {mostrarLista ? "Ocultar desglose" : "Mostrar desglose"}
                        </Button>
                    </div>

                    {mostrarLista && (
                        loadingCitas ? (
                            <p className="text-muted text-center" style={{ fontStyle: "italic" }}>Cargando desglose de masajes...</p>
                        ) : citasOrdenadas.length === 0 ? (
                            <p className="text-muted" style={{ fontStyle: "italic" }}>No hay masajes registrados para este mes.</p>
                        ) : (
                            <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }} className="shadow-sm">
                                <Table hover responsive size="sm" className="m-0" style={{ fontSize: "0.9rem" }}>
                                    <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                        <tr>
                                            <th>Fecha / Hora</th>
                                            <th>Cliente</th>
                                            <th>Tipo de Masaje</th>
                                            <th>Duración</th>
                                            <th className="text-end">Precio Est.</th>
                                            <th className="text-center">Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {citasOrdenadas.map((cita) => {
                                            const precio = obtenerPrecioCita(cita);
                                            const duracionText = obtenerDuracionCita(cita);
                                            return (
                                                <tr key={cita.id} style={{ verticalAlign: "middle" }}>
                                                    <td>
                                                        {new Date(cita.hora).toLocaleString("es-ES", {
                                                            day: "2-digit",
                                                            month: "2-digit",
                                                            hour: "2-digit",
                                                            minute: "2-digit"
                                                        })}
                                                    </td>
                                                    <td style={{ fontWeight: "500" }}>
                                                        {cita.nombre} {cita.primer_apellido}
                                                    </td>
                                                    <td>{cita.tipo_de_masaje}</td>
                                                    <td>{duracionText}</td>
                                                    <td className="text-end" style={{ fontWeight: "600", color: "#333" }}>
                                                        {precio}€
                                                    </td>
                                                    <td className="text-center">
                                                        <span 
                                                            className={`badge ${cita.ha_pagado ? "bg-success" : "bg-warning text-dark"}`}
                                                            style={{ padding: "5px 10px", minWidth: "75px" }}
                                                        >
                                                            {cita.ha_pagado ? "Pagada" : "Pendiente"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        )
                    )}
                </div>

            </Card.Body>
        </Card>
    );
};

export default AdminEstadisticas;